import { storeToRefs } from "pinia";

import { CallSessionStatus } from "@/enums/communicationLogs";
import { canShowNewLogNotification } from "@/helpers/communication";
import { useCallsStore } from "@/stores/calls";
import { useCommunicationStore } from "@/stores/communication";
import useSmsStore from "@/stores/sms";

import type {
  CallSessionEventData,
  CommunicationLogEventData,
  SmsMessageEventData,
} from "@/models/communicationLogs";

export const handleCommunicationLogsUpdate = (
  log: CommunicationLogEventData
) => {
  if (log.communication_log_id === null) {
    return;
  }

  const communicationStore = useCommunicationStore();
  const { hasNewLogs } = storeToRefs(communicationStore);

  if (log.event === "updated") {
    communicationStore.handleWebsocketLogUpdateEvent({
      id: log.communication_log_id,
      event: log.event,
      data: {
        ...log.data,
        type: log.type,
        application_id: String(log.linked_instance_id),
      },
    });
    return;
  }

  if (!canShowNewLogNotification(log.data) || log.event !== "indexed") {
    return;
  }

  hasNewLogs.value = true;
};

export const handleCallSessionUpdate = (
  callSessionEvent: CallSessionEventData
) => {
  const isOngoingOutgoingCallWithFromNumber = (
    callSessionEvent: CallSessionEventData
  ) => {
    return (
      callSessionEvent.data.status === CallSessionStatus.InProgress &&
      callSessionEvent.data.is_outgoing &&
      callSessionEvent.data.from
    );
  };

  const callStore = useCallsStore();
  const matchingId = callStore.activeCallSession?.data?.id;

  if (callSessionEvent.call_session_id !== matchingId) {
    return;
  }

  if (!isOngoingOutgoingCallWithFromNumber(callSessionEvent)) {
    return;
  }

  const fromNumber = callSessionEvent.data.from ?? undefined;

  if (callStore.activeCallSession?.data) {
    callStore.activeCallSession.data.from = fromNumber;
  }

  // Broadcast to other tabs so they can update their UI
  if (fromNumber) {
    callStore.broadcastPhoneNumberUpdate(fromNumber);
  }
};

export const handleSmsMessageEvent = (smsMessageEvent: SmsMessageEventData) => {
  const { incomingSmsEvents } = storeToRefs(useSmsStore());
  if (!incomingSmsEvents.value.length) {
    incomingSmsEvents.value = [smsMessageEvent];
    return;
  }
  const messageIndex = incomingSmsEvents.value.findIndex(
    (sms) => sms.sms_log_id === smsMessageEvent.sms_log_id
  );
  if (messageIndex > -1) {
    // prevent duplication
    return;
  }
  incomingSmsEvents.value.unshift(smsMessageEvent);
  return;
};
